/**
 * After a demo film goes up, ask `GET /media/{id}/status` until the server has finished with it, and
 * say once which way it went.
 *
 * The generated query, polled by TanStack's own `refetchInterval` (the phone's 2 s doubling to 15 s)
 * through a `QueryObserver` rather than `useQuery`: the watch starts in the save's handler and
 * outlives the wizard, and the answer it waits for is an EVENT — say „gotowy” once, re-read the
 * catalogue once — not a value to render. An observer's listener is where that event arrives; a
 * `useQuery` would need an effect to turn its data back into the same event.
 *
 * Returns the stop switch. A read that fails ends the watch as `unknown`: the film may well still
 * be processing, and not knowing is no reason to tell the trainer it failed — but neither is it a
 * reason to leave „Film jest przetwarzany” on the card for ever. The caller takes the note down
 * and re-reads the catalogue, which draws the film if the server has it.
 */

import { QueryObserver, type QueryClient } from '@tanstack/react-query';

import { getGetMediaIdStatusQueryOptions } from '~/shared/api/generated/endpoints/media/media';
import {
  mediaProcessingOutcome,
  processingPollDelayMs,
} from '~/shared/media/video/videoFile';

export function watchVideoProcessing(
  client: QueryClient,
  mediaId: number,
  onSettled: (outcome: 'ready' | 'failed' | 'unknown') => void,
): () => void {
  const observer = new QueryObserver(client, {
    ...getGetMediaIdStatusQueryOptions(mediaId),
    refetchInterval: (query) =>
      mediaProcessingOutcome(query.state.data?.status) === null
        ? processingPollDelayMs(query.state.dataUpdateCount)
        : false,
  });
  const unsubscribe = observer.subscribe((result) => {
    if (result.isError) {
      unsubscribe();
      onSettled('unknown');
      return;
    }
    const outcome = mediaProcessingOutcome(result.data?.status);
    if (outcome === null) return;
    unsubscribe();
    onSettled(outcome);
  });
  return unsubscribe;
}
